// Enum values are LiveKit's (livekit-client 2.x), so string comparisons and
// persisted values in the app keep their meaning.

export enum RoomEvent {
	Connected = 'connected',
	Reconnecting = 'reconnecting',
	SignalReconnecting = 'signalReconnecting',
	Reconnected = 'reconnected',
	Disconnected = 'disconnected',
	ConnectionStateChanged = 'connectionStateChanged',
	MediaDevicesChanged = 'mediaDevicesChanged',
	ParticipantConnected = 'participantConnected',
	ParticipantDisconnected = 'participantDisconnected',
	TrackPublished = 'trackPublished',
	TrackSubscribed = 'trackSubscribed',
	TrackSubscriptionFailed = 'trackSubscriptionFailed',
	TrackUnpublished = 'trackUnpublished',
	TrackUnsubscribed = 'trackUnsubscribed',
	TrackMuted = 'trackMuted',
	TrackUnmuted = 'trackUnmuted',
	LocalTrackPublished = 'localTrackPublished',
	LocalTrackUnpublished = 'localTrackUnpublished',
	ActiveSpeakersChanged = 'activeSpeakersChanged',
	ParticipantMetadataChanged = 'participantMetadataChanged',
	ParticipantNameChanged = 'participantNameChanged',
	ParticipantAttributesChanged = 'participantAttributesChanged',
	ParticipantPermissionsChanged = 'participantPermissionsChanged',
	DataReceived = 'dataReceived',
	ConnectionQualityChanged = 'connectionQualityChanged',
	TrackStreamStateChanged = 'trackStreamStateChanged',
	TrackSubscriptionStatusChanged = 'trackSubscriptionStatusChanged',
	AudioPlaybackStatusChanged = 'audioPlaybackChanged',
	MediaDevicesError = 'mediaDevicesError',
	ActiveDeviceChanged = 'activeDeviceChanged',
}

export enum ParticipantEvent {
	TrackPublished = 'trackPublished',
	TrackSubscribed = 'trackSubscribed',
	TrackSubscriptionFailed = 'trackSubscriptionFailed',
	TrackUnpublished = 'trackUnpublished',
	TrackUnsubscribed = 'trackUnsubscribed',
	TrackMuted = 'trackMuted',
	TrackUnmuted = 'trackUnmuted',
	LocalTrackPublished = 'localTrackPublished',
	LocalTrackUnpublished = 'localTrackUnpublished',
	ParticipantMetadataChanged = 'participantMetadataChanged',
	ParticipantNameChanged = 'participantNameChanged',
	DataReceived = 'dataReceived',
	IsSpeakingChanged = 'isSpeakingChanged',
	ConnectionQualityChanged = 'connectionQualityChanged',
	TrackSubscriptionStatusChanged = 'trackSubscriptionStatusChanged',
	AttributesChanged = 'attributesChanged',
	/** LiveKit reports each RTCRtpSender it creates; MoQ has none, so this never fires. */
	LocalSenderCreated = 'localSenderCreated',
}

export enum TrackEvent {
	Message = 'message',
	Muted = 'muted',
	Unmuted = 'unmuted',
	Restarted = 'restarted',
	Ended = 'ended',
	Subscribed = 'subscribed',
	Unsubscribed = 'unsubscribed',
	VisibilityChanged = 'visibilityChanged',
	VideoDimensionsChanged = 'videoDimensionsChanged',
	VideoPlaybackStarted = 'videoPlaybackStarted',
	VideoPlaybackFailed = 'videoPlaybackFailed',
	AudioPlaybackStarted = 'audioPlaybackStarted',
	AudioPlaybackFailed = 'audioPlaybackFailed',
	ElementAttached = 'elementAttached',
	ElementDetached = 'elementDetached',
	TrackProcessorUpdate = 'trackProcessorUpdate',
}

export enum ConnectionState {
	Disconnected = 'disconnected',
	Connecting = 'connecting',
	Connected = 'connected',
	Reconnecting = 'reconnecting',
	SignalReconnecting = 'signalReconnecting',
}

export enum ConnectionQuality {
	Excellent = 'excellent',
	Good = 'good',
	Poor = 'poor',
	Lost = 'lost',
	Unknown = 'unknown',
}

export enum VideoQuality {
	LOW = 0,
	MEDIUM = 1,
	HIGH = 2,
}

export enum BackupCodecPolicy {
	PREFER_REGRESSION = 0,
	SIMULCAST = 1,
	REGRESSION = 2,
}

export enum SubscriptionStatus {
	Desired = 'desired',
	Subscribed = 'subscribed',
	Unsubscribed = 'unsubscribed',
}

export enum PermissionStatus {
	Allowed = 'allowed',
	NotAllowed = 'not_allowed',
}

export type VideoCodec = 'vp8' | 'h264' | 'vp9' | 'av1' | 'h265';

export interface VideoEncoding {
	maxBitrate: number;
	maxFramerate?: number;
	priority?: RTCPriorityType;
}

export interface VideoResolution {
	width: number;
	height: number;
	frameRate?: number;
	aspectRatio?: number;
}

export class VideoPreset {
	readonly encoding: VideoEncoding;
	readonly width: number;
	readonly height: number;
	readonly aspectRatio?: number;

	constructor(width: number, height: number, maxBitrate: number, maxFramerate?: number, priority?: RTCPriorityType) {
		this.width = width;
		this.height = height;
		this.encoding = {maxBitrate, maxFramerate, priority};
	}

	get resolution(): VideoResolution {
		return {
			width: this.width,
			height: this.height,
			frameRate: this.encoding.maxFramerate,
			aspectRatio: this.aspectRatio,
		};
	}
}

export const VideoPresets = {
	h90: new VideoPreset(160, 90, 90_000, 20),
	h180: new VideoPreset(320, 180, 160_000, 20),
	h216: new VideoPreset(384, 216, 180_000, 20),
	h360: new VideoPreset(640, 360, 450_000, 20),
	h540: new VideoPreset(960, 540, 800_000, 25),
	h720: new VideoPreset(1280, 720, 1_700_000, 30),
	h1080: new VideoPreset(1920, 1080, 3_000_000, 30),
	h1440: new VideoPreset(2560, 1440, 5_000_000, 30),
	h2160: new VideoPreset(3840, 2160, 8_000_000, 30),
} as const;
